import { AxiosError } from 'axios';
import { Dispatch, SetStateAction } from 'react';
import { ApiErrorCode } from './ApiError';

const FORBIDDEN_FALLBACK =
  "You don't have permission to do that. If you think this is wrong, contact a Kit Keeper admin.";
const UNAUTHORIZED_FALLBACK =
  'Your session has expired. Please sign in again to continue.';
const GENERIC_FALLBACK = 'An unexpected error occurred. Please try again.';

const IS_DEBUG =
  process.env.NODE_ENV === 'development' ||
  (typeof window !== 'undefined' &&
    window.location?.hostname === 'test.kitkeeper.co.uk');

function readStoredFailedRequestId(): string | undefined {
  try {
    const value = localStorage.getItem('last_failed_request_id');
    return value ? value : undefined;
  } catch {
    return undefined;
  }
}

function messageFromCode(code: ApiErrorCode, requestId?: string): string {
  switch (code) {
    case 'UNAUTHENTICATED':
      return UNAUTHORIZED_FALLBACK;
    case 'FORBIDDEN':
      return FORBIDDEN_FALLBACK;
    case 'NOT_FOUND':
      return 'The item you were looking for could not be found.';
    case 'VALIDATION_FAILED':
      return "Please check the details you've entered and try again.";
    case 'CONFLICT':
      return 'This item was updated by someone else. Please refresh and try again.';
    case 'RATE_LIMITED':
      return 'Too many requests. Please wait a moment and try again.';
    case 'EXTERNAL_FAILURE':
      return "We couldn't reach an external service right now. Please try again in a moment.";
    case 'INTERNAL_ERROR':
      return requestId
        ? `Something went wrong on the server. Please try again. Reference: ${requestId}.`
        : 'Something went wrong on the server. Please try again.';
    case 'MAINTENANCE':
      return 'Kit Keeper is currently undergoing maintenance. Please try again shortly.';
  }
}

function serverFallback(requestId?: string): string {
  return requestId
    ? `Something went wrong on the server. Please try again. Reference: ${requestId}.`
    : 'Something went wrong on the server. Please try again.';
}

export default function errorMessage(
  setNetworkError: Dispatch<SetStateAction<string | undefined>>,
) {
  return (error: string | Error | AxiosError) => {
    const jsonError = JSON.parse(JSON.stringify(error));
    let userMessage: string;
    let technicalDetail: string | undefined;

    if (jsonError.hasOwnProperty('config')) {
      const axiosError = error as AxiosError;
      const status = axiosError.response?.status;
      const data = axiosError.response?.data;
      const code: ApiErrorCode | undefined = data?.code;
      const envelopeRequestId: string | undefined = data?.requestId;
      const backendMessage: string | undefined = data?.error || data?.message;

      technicalDetail = backendMessage;

      if (code) {
        userMessage = messageFromCode(code, envelopeRequestId);
      } else if (status && status >= 500) {
        const fallbackRequestId =
          envelopeRequestId || readStoredFailedRequestId();
        userMessage = serverFallback(fallbackRequestId);
      } else if (status === 403) {
        userMessage = backendMessage || FORBIDDEN_FALLBACK;
      } else if (status === 401) {
        userMessage = backendMessage || UNAUTHORIZED_FALLBACK;
      } else {
        userMessage = backendMessage || GENERIC_FALLBACK;
      }
    } else if (jsonError.hasOwnProperty('message')) {
      userMessage = (error as Error).message;
      technicalDetail = userMessage;
    } else {
      userMessage = error as string;
      technicalDetail = userMessage;
    }

    if (IS_DEBUG && technicalDetail && technicalDetail !== userMessage) {
      console.error('[errorMessage] technical detail:', technicalDetail);
    } else if (IS_DEBUG) {
      console.error(userMessage);
    }

    setNetworkError(userMessage);
  };
}
